import { BaseService, ServiceError, ValidationSchema } from './BaseService';
import { 
  systemConfigRepository, 
  SystemConfig 
} from '../repositories/SystemConfigRepository';

/**
 * Input type for creating/updating system config
 */
export interface UpsertSystemConfigInput {
  auto_call_distribution?: boolean;
  ring_strategy?: SystemConfig['ring_strategy'];
  max_ring_time?: number;
  voicemail_enabled?: boolean;
  callback_enabled?: boolean;
  quality_monitoring?: boolean;
  ai_assistance?: boolean;
  real_time_transcription?: boolean;
  sentiment_analysis?: boolean;
  target_answer_rate?: number;
  target_quality_score?: number;
  max_avg_wait_time?: number;
}

/**
 * Service for managing call center system configuration
 * Handles business logic and validation for system settings
 * NOTE: Each company has exactly one system config
 */
export class SystemConfigService extends BaseService {
  /**
   * Validation schema for system config
   */
  private configSchema: ValidationSchema = {
    max_ring_time: {
      required: false,
      type: 'number',
      min: 10,
      max: 120,
      message: 'Max ring time must be between 10 and 120 seconds'
    },
    target_answer_rate: {
      required: false,
      type: 'number',
      min: 0,
      max: 100,
      message: 'Target answer rate must be between 0 and 100 percent'
    },
    target_quality_score: {
      required: false,
      type: 'number',
      min: 0,
      max: 5,
      message: 'Target quality score must be between 0 and 5'
    },
    max_avg_wait_time: {
      required: false,
      type: 'number',
      min: 0,
      max: 600,
      message: 'Max average wait time must be between 0 and 600 seconds'
    }
  };

  /**
   * Get system config for a company
   */
  async getConfig(companyId: string): Promise<SystemConfig | null> {
    try {
      return await systemConfigRepository.findByCompany(companyId);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Create or update system config for a company
   */
  async upsertConfig(
    companyId: string,
    input: UpsertSystemConfigInput
  ): Promise<SystemConfig> {
    try {
      // Validate input
      if (Object.keys(input).length === 0) {
        throw ServiceError.validation([{
          field: 'input',
          message: 'At least one field must be provided'
        }]);
      }

      this.validate(input, this.configSchema);

      // Validate ring strategy if provided
      if (input.ring_strategy) {
        const validStrategies = ['round_robin', 'least_recent', 'skill_based', 'priority'];
        if (!validStrategies.includes(input.ring_strategy)) {
          throw ServiceError.validation([{
            field: 'ring_strategy',
            message: 'Invalid ring strategy'
          }]);
        }
      }

      // Upsert config
      const config = await systemConfigRepository.upsert(companyId, input);

      // Emit event
      const existing = await systemConfigRepository.findByCompany(companyId);
      const event = existing ? 'system.config.updated' : 'system.config.created';
      await this.emit(event, { config });

      return config;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Toggle auto call distribution
   */
  async toggleACD(companyId: string): Promise<SystemConfig> {
    try {
      const config = await systemConfigRepository.toggleACD(companyId);
      await this.emit('system.acd.toggled', { config });
      return config;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Update ring strategy
   */
  async updateRingStrategy(
    companyId: string,
    strategy: SystemConfig['ring_strategy']
  ): Promise<SystemConfig> {
    try {
      const validStrategies = ['round_robin', 'least_recent', 'skill_based', 'priority'];
      if (!validStrategies.includes(strategy)) {
        throw ServiceError.validation([{
          field: 'strategy',
          message: 'Invalid ring strategy'
        }]);
      }

      const config = await systemConfigRepository.updateRingStrategy(companyId, strategy);
      await this.emit('system.ring_strategy.updated', { config });
      return config;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Update feature toggles
   */
  async updateFeatures(
    companyId: string,
    features: {
      voicemail_enabled?: boolean;
      callback_enabled?: boolean;
      quality_monitoring?: boolean;
      ai_assistance?: boolean;
      real_time_transcription?: boolean;
      sentiment_analysis?: boolean;
    }
  ): Promise<SystemConfig> {
    try {
      if (Object.keys(features).length === 0) {
        throw ServiceError.validation([{
          field: 'features',
          message: 'At least one feature must be specified'
        }]);
      }

      const config = await systemConfigRepository.updateFeatures(companyId, features);
      await this.emit('system.features.updated', { config, features });
      return config;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Update performance targets
   */
  async updateTargets(
    companyId: string,
    targets: {
      target_answer_rate?: number;
      target_quality_score?: number;
      max_avg_wait_time?: number;
    }
  ): Promise<SystemConfig> {
    try {
      if (Object.keys(targets).length === 0) {
        throw ServiceError.validation([{
          field: 'targets',
          message: 'At least one target must be specified'
        }]);
      }

      // Validate targets
      if (targets.target_answer_rate !== undefined) {
        if (targets.target_answer_rate < 0 || targets.target_answer_rate > 100) {
          throw ServiceError.validation([{
            field: 'target_answer_rate',
            message: 'Target answer rate must be between 0 and 100 percent'
          }]);
        }
      }

      if (targets.target_quality_score !== undefined) {
        if (targets.target_quality_score < 0 || targets.target_quality_score > 5) {
          throw ServiceError.validation([{
            field: 'target_quality_score',
            message: 'Target quality score must be between 0 and 5'
          }]);
        }
      }

      if (targets.max_avg_wait_time !== undefined) {
        if (targets.max_avg_wait_time < 0) {
          throw ServiceError.validation([{
            field: 'max_avg_wait_time',
            message: 'Max average wait time must be positive'
          }]);
        }
      }

      const config = await systemConfigRepository.updateTargets(companyId, targets);
      await this.emit('system.targets.updated', { config, targets });
      return config;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Update max ring time
   */
  async updateMaxRingTime(
    companyId: string,
    seconds: number
  ): Promise<SystemConfig> {
    try {
      if (seconds < 10 || seconds > 120) {
        throw ServiceError.validation([{
          field: 'max_ring_time',
          message: 'Max ring time must be between 10 and 120 seconds'
        }]);
      }

      const config = await systemConfigRepository.updateMaxRingTime(companyId, seconds);
      await this.emit('system.max_ring_time.updated', { config });
      return config;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get system config with defaults if not exists
   * Useful for first-time setup
   */
  async getOrCreateDefaultConfig(companyId: string): Promise<SystemConfig> {
    try {
      let config = await this.getConfig(companyId);
      
      if (!config) {
        // Create default config
        config = await this.upsertConfig(companyId, {
          auto_call_distribution: true,
          ring_strategy: 'round_robin',
          max_ring_time: 30,
          voicemail_enabled: true,
          callback_enabled: true,
          quality_monitoring: true,
          ai_assistance: true,
          real_time_transcription: true,
          sentiment_analysis: true,
          target_answer_rate: 95,
          target_quality_score: 4.5,
          max_avg_wait_time: 60
        });

        await this.emit('system.config.initialized', { config });
      }

      return config;
    } catch (error) {
      this.handleError(error);
    }
  }
}

// Export singleton instance
export const systemConfigService = new SystemConfigService();
